/**
 * Scope Docs tab — the project/customer/aeronet wiki, hub-style:
 * grouped left nav (ScopeLanding's sections mirrored) · native React landing
 * (ScopeLanding, no iframe) · seamless inline-rendered HTML docs · markdown
 * docs via the shared renderer · highlight-to-comment feedback rail.
 * Feedback threads ride the plan-threads store with anchor.docKey (no diffs,
 * no proposals — the AI reads threads via MCP and edits the docs itself).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { EmptyState, Icon, StatusPill } from "../../../mui";
import { fmtBytes } from "../../../mui/format";
import { brainTokens } from "../../../design/brainTheme";
import {
  getObjectText,
  listFiles,
  getPlan,
  addPlanThread,
  replyPlanThread,
  setPlanThreadStatus,
  presignFile,
  type PlanThread,
  type StoreObject,
  type StoreTags,
} from "../../../lib/brain-api";
import { MarkdownDocument, markdownToc } from "../../../components/MarkdownDocument";
import { SandboxedDocViewer, resolveDocHref } from "../../../components/SandboxedDocViewer";
import { FileViewer } from "../../../components/FileViewer";
import { resolveSectionGroups, type ScopeIndex, type ScopeIndexEntry } from "./scopeIndexModel";
import { ScopeLanding } from "./ScopeLanding";
import { ProjectFilesTabMui } from "../ProjectFilesTabMui";

export type { ScopeIndex, ScopeIndexEntry } from "./scopeIndexModel";

const c = brainTokens.color;

const LANDING = "index.html";
const FILES_NAV_KEY = "__files__";

type DocsPane =
  | { kind: "docs" }
  | { kind: "file"; rel: string }
  | { kind: "manage" };

function jiraUrl(key: string): string {
  return `https://aeronetnz.atlassian.net/browse/${encodeURIComponent(key.toUpperCase())}`;
}

function isMd(key: string): boolean {
  return /\.(md|markdown)$/i.test(key);
}

/** Raw store files for the Files nav — not index/landing, not docs/* wiki pages. */
function isRawNavFile(rel: string): boolean {
  if (!rel || rel.endsWith("/") || rel === ".keep" || rel.endsWith("/.keep")) return false;
  if (rel === "index.json" || rel === "index.html") return false;
  if (rel.startsWith("docs/")) return false;
  return true;
}

function tagsForPrefix(prefix: string): StoreTags {
  if (prefix.startsWith("projects/")) {
    const id = prefix.slice("projects/".length).replace(/\/$/, "");
    return { project: id, source: "upload" };
  }
  if (prefix.startsWith("customers/")) {
    const id = prefix.slice("customers/".length).replace(/\/$/, "");
    return { customer: id, source: "upload", tier: "company" };
  }
  return { source: "upload" };
}

function fileKind(name: string): "renderable" | "other" {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (["md", "markdown", "mdx", "html", "htm", "png", "jpg", "jpeg", "gif", "svg", "webp", "avif", "bmp", "pdf", "txt", "log", "json", "yaml", "yml", "csv", "sql", "sh", "ts", "js", "py"].includes(ext)) {
    return "renderable";
  }
  return "other";
}

export function ScopeDocsTab({
  prefix,
  label,
  openKey,
  onOpenKey,
}: {
  /** Store prefix ending with `/`, e.g. `projects/foo/` or `customers/bar/`. */
  prefix: string;
  /** Human label for empty states */
  label: string;
  /** Optional deep-link doc path relative to prefix (e.g. `docs/workstreams.html`) */
  openKey?: string | null;
  onOpenKey?: (relKey: string | null) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [index, setIndex] = useState<ScopeIndex | null>(null);
  const [docText, setDocText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState<string>(LANDING); // rel key; LANDING = index.html
  const [fallbackDocs, setFallbackDocs] = useState<string[]>([]);
  const [storeObjects, setStoreObjects] = useState<StoreObject[]>([]);
  const [navExpanded, setNavExpanded] = useState<Set<string>>(new Set());
  const [pane, setPane] = useState<DocsPane>({ kind: "docs" });
  const [navSearch, setNavSearch] = useState("");

  // Feedback rail state (projects only — threads live on the project record).
  const projectId = prefix.startsWith("projects/") ? prefix.slice("projects/".length).replace(/\/$/, "") : null;
  const [threads, setThreads] = useState<PlanThread[]>([]);
  const [railOpen, setRailOpen] = useState(false);
  const [selection, setSelection] = useState("");
  const [draft, setDraft] = useState("");
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const setOpen = useCallback(
    (rel: string | null) => {
      const key = rel && rel !== LANDING ? rel : LANDING;
      setPane({ kind: "docs" });
      setActive(key);
      setSelection("");
      onOpenKey?.(key === LANDING ? null : key);
    },
    [onOpenKey]
  );

  const openFile = useCallback((rel: string) => {
    setPane({ kind: "file", rel });
    setSelection("");
    setNavExpanded((prev) => (prev.has(FILES_NAV_KEY) ? prev : new Set(prev).add(FILES_NAV_KEY)));
  }, []);

  const openManage = useCallback(() => {
    setPane({ kind: "manage" });
    setSelection("");
    setNavExpanded((prev) => (prev.has(FILES_NAV_KEY) ? prev : new Set(prev).add(FILES_NAV_KEY)));
  }, []);

  const loadIndex = useCallback(async () => {
    setLoading(true);
    try {
      const [idxRaw, files] = await Promise.all([
        getObjectText(`${prefix}index.json`).catch(() => ""),
        listFiles(prefix).catch(() => [] as StoreObject[]),
      ]);
      let parsed: ScopeIndex | null = null;
      if (idxRaw?.trim()) {
        try {
          parsed = JSON.parse(idxRaw) as ScopeIndex;
        } catch {
          parsed = null;
        }
      }
      setIndex(parsed);
      setStoreObjects(files);
      const docs = files
        .map((f) => f.key)
        .filter((k) => k.startsWith(`${prefix}docs/`) && /\.(html?|md|markdown)$/i.test(k))
        .map((k) => k.slice(prefix.length));
      setFallbackDocs(docs);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to load docs", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [prefix, enqueueSnackbar]);

  const loadThreads = useCallback(async () => {
    if (!projectId) return;
    try {
      const bundle = await getPlan(projectId);
      setThreads(bundle.threads ?? []);
    } catch {
      setThreads([]);
    }
  }, [projectId]);

  useEffect(() => {
    void loadIndex();
    void loadThreads();
  }, [loadIndex, loadThreads]);

  useEffect(() => {
    setActive(openKey || LANDING);
    setPane({ kind: "docs" });
  }, [openKey]);

  // Load the active doc's text (any non-landing doc; landing is native React).
  useEffect(() => {
    if (pane.kind !== "docs" || active === LANDING) {
      setDocText(null);
      return;
    }
    let cancelled = false;
    setDocText(null);
    (async () => {
      try {
        const text = await getObjectText(`${prefix}${active}`);
        if (!cancelled) setDocText(text);
      } catch {
        if (!cancelled) enqueueSnackbar(`Could not load ${active}`, { variant: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, prefix, enqueueSnackbar, pane.kind]);

  // ---- nav model ------------------------------------------------------------
  const groups = useMemo(() => resolveSectionGroups(index, fallbackDocs), [index, fallbackDocs]);
  const navGroups = useMemo(() => groups.filter((g) => g.section.layout !== "links"), [groups]);
  const linkGroups = useMemo(() => groups.filter((g) => g.section.layout === "links"), [groups]);

  const filteredNavGroups = useMemo(() => {
    const q = navSearch.trim().toLowerCase();
    if (!q) return navGroups;
    return navGroups
      .map((g) => ({ ...g, entries: g.entries.filter((e) => e.title.toLowerCase().includes(q)) }))
      .filter((g) => g.entries.length > 0);
  }, [navGroups, navSearch]);

  const rawFiles = useMemo(() => {
    return storeObjects
      .map((o) => ({ ...o, rel: o.key.startsWith(prefix) ? o.key.slice(prefix.length) : o.key }))
      .filter((o) => isRawNavFile(o.rel))
      .sort((a, b) => a.rel.localeCompare(b.rel));
  }, [storeObjects, prefix]);

  const fileTags = useMemo(() => tagsForPrefix(prefix), [prefix]);

  // After Manage uploads/moves, refresh the Files nav list without full-page reload flash.
  useEffect(() => {
    if (pane.kind !== "manage") return;
    return () => {
      void listFiles(prefix)
        .then(setStoreObjects)
        .catch(() => undefined);
    };
  }, [pane.kind, prefix]);

  const toggleNavGroup = (key: string) =>
    setNavExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Auto-expand a collapsed group when the active doc lives inside it.
  useEffect(() => {
    if (pane.kind !== "docs") return;
    for (const g of navGroups) {
      if (g.section.collapsed && g.entries.some((e) => e.key === active)) {
        setNavExpanded((prev) => (prev.has(g.section.key) ? prev : new Set(prev).add(g.section.key)));
      }
    }
  }, [active, navGroups, pane.kind]);

  const activeInfo = useMemo(() => {
    if (pane.kind === "manage") return { sectionTitle: "Files" as string | null, docTitle: "Manage files" };
    if (pane.kind === "file") {
      const name = pane.rel.split("/").pop() ?? pane.rel;
      return { sectionTitle: "Files" as string | null, docTitle: name };
    }
    if (active === LANDING) return { sectionTitle: null as string | null, docTitle: "Index" };
    for (const g of navGroups) {
      const entry = g.entries.find((e) => e.key === active);
      if (entry) return { sectionTitle: g.section.title, docTitle: entry.title };
    }
    return { sectionTitle: null as string | null, docTitle: active };
  }, [active, navGroups, pane]);

  const onNavigate = (href: string) => {
    const base = active === LANDING ? `${prefix}${LANDING}` : `${prefix}${active}`;
    if (/(^|\/)index\.html$/i.test(href) || href === "./" || href === ".") {
      setOpen(null);
      return;
    }
    const absolute = resolveDocHref(base, href);
    if (!absolute || !absolute.startsWith(prefix)) {
      enqueueSnackbar(`Link outside scope: ${href}`, { variant: "warning" });
      return;
    }
    setOpen(absolute.slice(prefix.length));
  };

  const openLink = (e: ScopeIndexEntry) => {
    if (e.kind === "jira") window.open(jiraUrl(e.key), "_blank", "noopener");
    else if (e.kind === "external" && /^https?:/i.test(e.key)) window.open(e.key, "_blank", "noopener");
    else if (e.kind === "external") enqueueSnackbar(`Repo: ${e.key}`, { variant: "info" });
    else enqueueSnackbar(`Related scope: ${e.key.replace(/^scope:/, "")}`, { variant: "info" });
  };

  // ---- feedback -------------------------------------------------------------
  const docThreads = useMemo(() => {
    const rel = active;
    return threads.filter((t) => {
      const dk = t.target?.anchor?.docKey;
      if (dk) return dk === rel;
      // Legacy plan-anchored threads show on plan.md.
      return rel === "plan.md" && t.target?.doc === "canonical";
    });
  }, [threads, active]);

  const openThreadCount = docThreads.filter((t) => t.status === "open").length;

  const toc = useMemo(() => {
    if (pane.kind !== "docs" || active === LANDING || !docText || !isMd(active)) return [];
    return markdownToc(docText);
  }, [pane.kind, active, docText]);
  const showRightRail = pane.kind === "docs" && active !== LANDING && (toc.length > 0 || !!projectId);

  const submitFeedback = async () => {
    if (!projectId || !draft.trim()) return;
    setBusy(true);
    try {
      await addPlanThread({
        project: projectId,
        type: "feedback",
        body: draft.trim(),
        target: { anchor: { docKey: active, quotedText: selection || undefined } },
      });
      setDraft("");
      setSelection("");
      await loadThreads();
      enqueueSnackbar("Feedback added.", { variant: "success" });
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to add feedback", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const submitReply = async (threadId: string) => {
    const body = replyDrafts[threadId]?.trim();
    if (!projectId || !body) return;
    setBusy(true);
    try {
      await replyPlanThread({ project: projectId, threadId, body });
      setReplyDrafts((d) => ({ ...d, [threadId]: "" }));
      await loadThreads();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to reply", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const resolveThread = async (threadId: string) => {
    if (!projectId) return;
    setBusy(true);
    try {
      await setPlanThreadStatus({ project: projectId, threadId, status: "resolved", reason: "resolved from Docs" });
      await loadThreads();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to resolve", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  // Selection inside markdown docs (rendered in the parent DOM, not the iframe).
  const onContentMouseUp = () => {
    const sel = window.getSelection()?.toString().trim() ?? "";
    if (sel) {
      setSelection(sel);
      setRailOpen(true);
    }
  };

  // ---- render ---------------------------------------------------------------
  if (loading) return <Box sx={{ fontSize: 13, color: c.muted2 }}>Loading docs…</Box>;

  const hasAnything = Boolean(index || fallbackDocs.length || rawFiles.length);
  if (!hasAnything) {
    return (
      <EmptyState icon="book" title={`No docs yet for ${label}`}>
        Author <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12 }}>index.json</Box> +{" "}
        <Box component="span" sx={{ fontFamily: brainTokens.font.mono, fontSize: 12 }}>docs/*.html</Box> with the
        brain-doc skill, then /brain-wrap.
      </EmptyState>
    );
  }

  const filesNavOpen = navExpanded.has(FILES_NAV_KEY);
  const activeFileRel = pane.kind === "file" ? pane.rel : null;

  const navItem = (key: string, title: string, activeNow: boolean, onClick: () => void, indent = false) => (
    <Box
      key={key}
      onClick={onClick}
      sx={{
        fontSize: 13,
        lineHeight: 1.35,
        px: 1.25,
        py: 0.75,
        ml: indent ? 1 : 0,
        borderRadius: "6px",
        cursor: "pointer",
        color: activeNow ? c.tealInk : c.ink2,
        background: activeNow ? c.tealTint : "transparent",
        fontWeight: activeNow ? 650 : 450,
        "&:hover": { background: activeNow ? c.tealTint : c.panelBg2 },
      }}
    >
      {title}
    </Box>
  );

  const openRawFile = async (fileKey: string) => {
    try {
      window.open(await presignFile(fileKey), "_blank", "noopener");
    } catch {
      enqueueSnackbar("Could not open this file.", { variant: "error" });
    }
  };

  // Doc switcher rail only shows once a doc/file is open — the landing
  // (library) view is full-width, per the redesign's "reading view only" rail.
  const showRail = pane.kind !== "docs" || active !== LANDING;
  const totalDocsCount = navGroups.reduce((n, g) => n + g.entries.length, 0);

  return (
    <Box sx={{ background: c.pageBg, borderRadius: "10px", ml: "-24px", mr: "-40px", pl: "24px", pr: "40px", py: 2 }}>
      <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start" }}>
        {/* Doc switcher rail — grey, shown only while reading (not on the library landing). */}
        {showRail && (
        <Box
          sx={{
            width: 250,
            flex: "none",
            position: "sticky",
            top: 16,
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto",
            background: c.panelBg3,
            border: `1px solid ${c.border}`,
            borderRadius: "8px",
            padding: "14px",
          }}
        >
          <Box
            onClick={() => setOpen(null)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: 12.5,
              fontWeight: 500,
              color: c.ink3,
              cursor: "pointer",
              mb: "10px",
              "&:hover": { color: c.tealInk },
            }}
          >
            <Icon name="arrow-left" size={13} />
            All {totalDocsCount} docs
          </Box>
          <Box sx={{ position: "relative", mb: "12px" }}>
            <Box sx={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: c.muted2, display: "flex" }}>
              <Icon name="search" size={14} />
            </Box>
            <input
              value={navSearch}
              onChange={(e) => setNavSearch(e.target.value)}
              placeholder="Search in this project"
              style={{
                width: "100%",
                boxSizing: "border-box",
                background: c.white,
                border: `1px solid ${c.border}`,
                borderRadius: "7px",
                padding: "7px 10px 7px 32px",
                fontSize: 12.5,
                color: c.ink2,
                fontFamily: brainTokens.font.sans,
              }}
            />
          </Box>
          {filteredNavGroups.map((g) => {
            const isCurrentGroup = g.entries.some((e) => e.key === active);
            const collapsed = g.section.collapsed === true && !navSearch.trim();
            const isOpen = !collapsed || navExpanded.has(g.section.key) || isCurrentGroup;
            return (
              <Box key={g.section.key}>
                {isCurrentGroup ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: c.tealInk,
                      background: c.tealTint,
                      borderRadius: "7px",
                      padding: "9px 11px",
                      mt: "6px",
                      mb: "4px",
                      boxShadow: `inset 3px 0 0 ${c.teal}`,
                    }}
                  >
                    {g.section.title}
                  </Box>
                ) : collapsed ? (
                  <Box
                    onClick={() => toggleNavGroup(g.section.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: ".05em",
                      color: c.muted2,
                      mt: 1.5,
                      mb: 0.5,
                      px: 1.25,
                      cursor: "pointer",
                    }}
                  >
                    <Icon name={isOpen ? "chevron-down" : "chevron-right"} size={11} />
                    {g.section.title} ({g.entries.length})
                  </Box>
                ) : (
                  <Box sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: c.muted2, mt: 1.5, mb: 0.5, px: 1.25 }}>
                    {g.section.title}
                  </Box>
                )}
                {isOpen && g.entries.map((e) => navItem(e.key, e.title, pane.kind === "docs" && active === e.key, () => setOpen(e.key)))}
              </Box>
            );
          })}

          {/* Pinned Files group — raw store files; above Links. Collapsed by default. */}
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: ".05em",
                color: c.muted2,
                mt: 1.5,
                mb: 0.5,
                px: 1.25,
              }}
            >
              <Box
                onClick={() => toggleNavGroup(FILES_NAV_KEY)}
                sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", flex: 1, minWidth: 0 }}
              >
                <Icon name={filesNavOpen ? "chevron-down" : "chevron-right"} size={11} />
                Files ({rawFiles.length})
              </Box>
              <Box
                component="span"
                onClick={(ev) => {
                  ev.stopPropagation();
                  openManage();
                }}
                sx={{
                  textTransform: "none",
                  letterSpacing: 0,
                  fontSize: 11,
                  color: pane.kind === "manage" ? c.tealInk : c.muted,
                  cursor: "pointer",
                  fontWeight: pane.kind === "manage" ? 650 : 450,
                  "&:hover": { color: c.tealInk },
                  flex: "none",
                }}
              >
                Manage files
              </Box>
            </Box>
            {filesNavOpen && (
              <>
                {rawFiles.length === 0 && (
                  <Box sx={{ fontSize: 12, color: c.faint, px: 1.25, py: 0.5 }}>No files yet</Box>
                )}
                {rawFiles.map((f) => {
                  const name = f.rel.split("/").pop() ?? f.rel;
                  return navItem(`file:${f.rel}`, name, activeFileRel === f.rel, () => openFile(f.rel));
                })}
              </>
            )}
          </Box>

          {linkGroups.map((g) => (
            <Box key={g.section.key}>
              <Box sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: c.muted2, mt: 1.5, mb: 0.5, px: 1.25 }}>
                {g.section.title}
              </Box>
              {g.entries.map((e) => (
                <Box
                  key={`${e.kind}:${e.key}`}
                  onClick={() => openLink(e)}
                  sx={{
                    fontSize: 12.5,
                    px: 1.25,
                    py: 0.6,
                    borderRadius: "6px",
                    cursor: "pointer",
                    color: c.muted,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    "&:hover": { background: c.panelBg2, color: c.tealInk },
                  }}
                >
                  <Icon name={e.kind === "jira" ? "clipboard-list" : "external-link"} size={12} />
                  <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.title}</Box>
                </Box>
              ))}
            </Box>
          ))}
          {index && (
            <Box sx={{ mt: 2, px: 1.25, fontSize: 11.5, color: c.faint }}>Updated {index.updatedAt}</Box>
          )}
        </Box>
        )}

        {/* Content */}
        <Box ref={contentRef} onMouseUp={onContentMouseUp} sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontSize: 12.5, color: c.muted }}>
                {label}{activeInfo.sectionTitle ? ` / ${activeInfo.sectionTitle}` : ""}
              </Box>
              <Box sx={{ fontSize: 18, fontWeight: 700, color: c.ink, mt: 0.25 }}>{activeInfo.docTitle}</Box>
            </Box>
          </Box>
  
          {pane.kind === "manage" ? (
            <ProjectFilesTabMui
              prefix={prefix}
              tags={fileTags}
              onOpenPlan={() => setOpen("plan.md")}
            />
          ) : pane.kind === "file" ? (
            (() => {
              const fileObj = rawFiles.find((f) => f.rel === pane.rel);
              const name = pane.rel.split("/").pop() ?? pane.rel;
              if (fileKind(name) === "other") {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      p: "14px 16px",
                      border: `1px solid ${c.borderLight}`,
                      borderRadius: "8px",
                      fontSize: 14,
                    }}
                  >
                    <Box sx={{ color: c.muted2, display: "flex" }}>
                      <Icon name="book" size={18} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>
                      {name}
                    </Box>
                    {fileObj?.size != null && (
                      <Box sx={{ fontFamily: brainTokens.font.mono, fontSize: 12, color: c.faint, flex: "none" }}>
                        {fmtBytes(fileObj.size)}
                      </Box>
                    )}
                    <Button size="small" variant="outlined" endIcon={<Icon name="external-link" size={13} />} onClick={() => void openRawFile(`${prefix}${pane.rel}`)}>
                      Open raw
                    </Button>
                  </Box>
                );
              }
              return (
                <FileViewer
                  fileKey={`${prefix}${pane.rel}`}
                  name={name}
                  onNavigate={(absoluteKey) => {
                    if (!absoluteKey.startsWith(prefix)) {
                      enqueueSnackbar(`Link outside scope: ${absoluteKey}`, { variant: "warning" });
                      return;
                    }
                    const rel = absoluteKey.slice(prefix.length);
                    if (isRawNavFile(rel)) openFile(rel);
                    else setOpen(rel);
                  }}
                />
              );
            })()
          ) : active === LANDING ? (
            <ScopeLanding
              index={index}
              groups={groups}
              rawFiles={rawFiles}
              onOpen={setOpen}
              onOpenLink={openLink}
              onOpenFiles={openManage}
            />
          ) : docText == null ? (
            <Box sx={{ fontSize: 13, color: c.muted2 }}>Loading…</Box>
          ) : isMd(active) ? (
            <MarkdownDocument content={docText || "_Empty document._"} projectId={projectId ?? undefined} documentKey={`${prefix}${active}`} onOpenFile={key => {
              if (!key.startsWith(prefix)) return;
              const rel = key.slice(prefix.length);
              if (isRawNavFile(rel)) openFile(rel); else setOpen(rel);
            }}/>
          ) : (
            <SandboxedDocViewer
              html={docText}
              title={activeInfo.docTitle}
              onNavigate={onNavigate}
              onSelect={(t) => t && (setSelection(t), setRailOpen(true))}
              seamless
            />
          )}
        </Box>
  
        {/* Reading-view right rail — On this page + Feedback (highlight text, add a
            note; the AI reads threads via MCP). Always shown while reading; the
            Feedback compose/threads section toggles via railOpen. */}
        {showRightRail && (
          <Box
            sx={{
              width: 250,
              flex: "none",
              position: "sticky",
              top: 16,
              maxHeight: "calc(100vh - 120px)",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "14px",
            }}
          >
            {toc.length > 0 && (
              <Box sx={{ border: `1px solid ${c.borderLight}`, borderRadius: "8px", p: "12px 14px" }}>
                <Box
                  sx={{
                    fontFamily: brainTokens.font.mono,
                    fontSize: 10,
                    fontWeight: 600,
                    letterSpacing: ".1em",
                    color: c.faint,
                    mb: "8px",
                  }}
                >
                  ON THIS PAGE
                </Box>
                {toc.map((t) => (
                  <Box
                    key={t.slug}
                    component="a"
                    href={`#${t.slug}`}
                    sx={{
                      display: "block",
                      fontSize: 13,
                      color: c.muted,
                      textDecoration: "none",
                      py: "3px",
                      "&:hover": { color: c.tealInk },
                    }}
                  >
                    {t.title}
                  </Box>
                ))}
              </Box>
            )}

            {projectId && (
              <Box sx={{ border: `1px solid ${c.borderLight}`, borderRadius: "8px", p: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Box sx={{ fontSize: 13, fontWeight: 650, color: c.ink, flex: 1 }}>
                Feedback{openThreadCount ? ` (${openThreadCount})` : ""}
              </Box>
              <IconButton size="small" onClick={() => setRailOpen((o) => !o)}>
                <Icon name={railOpen ? "chevron-down" : "chevron-right"} size={14} />
              </IconButton>
            </Box>
            {railOpen && (
              <>
  
            {selection && (
              <Box
                sx={{
                  fontSize: 12,
                  color: c.muted,
                  borderLeft: `3px solid ${c.teal}`,
                  pl: 1,
                  mb: 1,
                  maxHeight: 88,
                  overflow: "hidden",
                  fontStyle: "italic",
                }}
              >
                “{selection.slice(0, 240)}{selection.length > 240 ? "…" : ""}”
              </Box>
            )}
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder={selection ? "Comment on the highlighted text…" : "Highlight text in the doc, or comment on the whole page…"}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              sx={{ mb: 1 }}
            />
            <Button size="small" variant="contained" fullWidth disabled={busy || !draft.trim()} onClick={() => void submitFeedback()}>
              Add feedback
            </Button>
  
            <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
              {docThreads.length === 0 && (
                <Box sx={{ fontSize: 12.5, color: c.muted2 }}>No feedback on this page yet.</Box>
              )}
              {docThreads.map((t) => (
                <Box key={t.sk} sx={{ border: `1px solid ${c.borderLight}`, borderRadius: "8px", background: c.pageBg, p: 1.25 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                    <StatusPill tone={t.status === "open" ? "warning" : "neutral"} label={t.status} />
                    <Box sx={{ fontSize: 11.5, color: c.faint, flex: 1 }}>{t.author?.displayName ?? ""}</Box>
                    {t.status === "open" && (
                      <Button size="small" sx={{ minWidth: 0, fontSize: 11 }} disabled={busy} onClick={() => void resolveThread(t.sk)}>
                        Resolve
                      </Button>
                    )}
                  </Box>
                  {t.target?.anchor?.quotedText && (
                    <Box sx={{ fontSize: 11.5, color: c.muted2, borderLeft: `2px solid ${c.border}`, pl: 0.75, mb: 0.5, fontStyle: "italic" }}>
                      {t.target.anchor.quotedText.slice(0, 160)}
                      {t.target.anchor.quotedText.length > 160 ? "…" : ""}
                    </Box>
                  )}
                  {(t.messages ?? []).map((m) => (
                    <Box key={m.id} sx={{ fontSize: 12.5, color: c.ink2, mb: 0.5 }}>
                      {m.body}
                    </Box>
                  ))}
                  {t.status === "open" && (
                    <Box sx={{ display: "flex", gap: 0.5, mt: 0.75 }}>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Reply…"
                        value={replyDrafts[t.sk] ?? ""}
                        onChange={(e) => setReplyDrafts((d) => ({ ...d, [t.sk]: e.target.value }))}
                        inputProps={{ style: { fontSize: 12.5 } }}
                      />
                      <Button size="small" disabled={busy || !(replyDrafts[t.sk] ?? "").trim()} onClick={() => void submitReply(t.sk)}>
                        Send
                      </Button>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
              </>
            )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
